/**
 * AuditLogPage — paginated audit log viewer with filters.
 *
 * Accessible to: admins + users with canViewAuditLogs permission.
 *
 * Filters:
 *   - Action (grouped dropdown)
 *   - Audience
 *   - Date range (from / to)
 *
 * Entries load 25 at a time; "Load more" fetches the next page via cursor.
 */

import { useState, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ClipboardList,
  Filter,
  X,
  ArrowRight,
  FileText,
  Music,
  Image,
  LogIn,
  LogOut,
  Users,
  Shield,
  Copy,
  Activity,
  Loader2,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuth }       from '@/hooks/useAuth';
import { useAuditLogs }  from '@/hooks/useAuditLogs';
import { AUDIENCES, AUDIENCE_LABELS } from '@/types/auth.types';
import type { Audience } from '@/types/auth.types';
import type { AuditLog, AuditAction, AuditLogFilters } from '@/types';
import { AUDIT_ACTION_LABELS, AUDIT_ACTION_GROUPS } from '@/types/audit.types';
import { formatDisplayDate } from '@/utils/formatUtils';
import Button from '@/components/common/Button';

// ─── Helpers ───────────────────────────────────────────────────────────────────

const AUDIENCE_DOT: Record<Audience, string> = {
  mothers:      'bg-pink-400',
  fathers:      'bg-blue-400',
  grandparents: 'bg-purple-400',
};

function getActionGroup(action: AuditAction): string {
  for (const [group, actions] of Object.entries(AUDIT_ACTION_GROUPS)) {
    if ((actions as AuditAction[]).includes(action)) return group;
  }
  return 'Other';
}

const GROUP_DOT: Record<string, string> = {
  'Authentication':  'bg-slate-400',
  'Content':         'bg-primary-400',
  'Media':           'bg-teal-400',
  'User Management': 'bg-orange-400',
  'Permissions':     'bg-violet-400',
  'Other':           'bg-surface-300',
};

function ActionIcon({ action }: { action: string }) {
  const cls = 'h-3.5 w-3.5';
  if (action.startsWith('content_cop')) return <Copy    className={cls} />;
  if (action.startsWith('content'))    return <FileText className={cls} />;
  if (action.startsWith('audio'))      return <Music    className={cls} />;
  if (action.startsWith('image'))      return <Image    className={cls} />;
  if (action === 'login')              return <LogIn    className={cls} />;
  if (action === 'logout')             return <LogOut   className={cls} />;
  if (action.startsWith('user'))       return <Users    className={cls} />;
  if (action.startsWith('permission') || action.startsWith('role'))
    return <Shield className={cls} />;
  return <Activity className={cls} />;
}

function parseLocalDate(value: string): Date | null {
  if (!value) return null;
  const d = new Date(value + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

function toEndOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function AuditLogPage() {
  const { isAdmin, canViewAuditLogs } = useAuth();

  // Permission guard
  if (!isAdmin && !canViewAuditLogs) {
    return <Navigate to="/dashboard" replace />;
  }

  // ── Filter state ─────────────────────────────────────────────────────────
  const [actionFilter,   setActionFilter]   = useState<AuditAction | ''>('');
  const [audienceFilter, setAudienceFilter] = useState<Audience | ''>('');
  const [dateFromStr,    setDateFromStr]    = useState('');
  const [dateToStr,      setDateToStr]      = useState('');

  const filters = useMemo<AuditLogFilters>(() => ({
    action:   actionFilter   || null,
    audience: audienceFilter || null,
    dateFrom: parseLocalDate(dateFromStr),
    dateTo:   dateToStr ? toEndOfDay(parseLocalDate(dateToStr)!) : null,
  }), [actionFilter, audienceFilter, dateFromStr, dateToStr]);

  const hasFilters = !!(actionFilter || audienceFilter || dateFromStr || dateToStr);

  const clearFilters = () => {
    setActionFilter('');
    setAudienceFilter('');
    setDateFromStr('');
    setDateToStr('');
  };

  // ── Query ────────────────────────────────────────────────────────────────
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAuditLogs(filters);

  const entries = useMemo(
    () => data?.pages.flatMap(p => p.items) ?? [],
    [data],
  );

  return (
    <div className="space-y-5 max-w-4xl">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50">
          <ClipboardList className="h-5 w-5 text-primary-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-surface-900">Audit Log</h1>
          <p className="text-xs text-surface-400">All CMS activity</p>
        </div>
      </div>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-surface-200 bg-white p-4 shadow-card">
        <div className="flex flex-wrap gap-3 items-end">

          {/* Action filter */}
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="text-[11px] font-semibold text-surface-400 uppercase tracking-wide">
              Action
            </label>
            <select
              value={actionFilter}
              onChange={e => setActionFilter(e.target.value as AuditAction | '')}
              className="rounded-lg border border-surface-300 bg-white px-3 py-2 text-sm
                         text-surface-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              <option value="">All actions</option>
              {Object.entries(AUDIT_ACTION_GROUPS).map(([group, actions]) => (
                <optgroup key={group} label={group}>
                  {(actions as AuditAction[]).map(a => (
                    <option key={a} value={a}>{AUDIT_ACTION_LABELS[a]}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Audience filter */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-surface-400 uppercase tracking-wide">
              Audience
            </label>
            <select
              value={audienceFilter}
              onChange={e => setAudienceFilter(e.target.value as Audience | '')}
              className="rounded-lg border border-surface-300 bg-white px-3 py-2 text-sm
                         text-surface-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
            >
              <option value="">All audiences</option>
              {AUDIENCES.map(a => (
                <option key={a} value={a}>{AUDIENCE_LABELS[a]}</option>
              ))}
            </select>
          </div>

          {/* Date from */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-surface-400 uppercase tracking-wide">
              From
            </label>
            <input
              type="date"
              value={dateFromStr}
              onChange={e => setDateFromStr(e.target.value)}
              className="rounded-lg border border-surface-300 bg-white px-3 py-2 text-sm
                         text-surface-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          {/* Date to */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-surface-400 uppercase tracking-wide">
              To
            </label>
            <input
              type="date"
              value={dateToStr}
              onChange={e => setDateToStr(e.target.value)}
              className="rounded-lg border border-surface-300 bg-white px-3 py-2 text-sm
                         text-surface-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          {/* Clear */}
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<X className="h-3.5 w-3.5" />}
              onClick={clearFilters}
              className="mb-0.5"
            >
              Clear
            </Button>
          )}
        </div>

        {hasFilters && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-surface-100">
            <Filter className="h-3.5 w-3.5 text-surface-400" />
            <p className="text-xs text-surface-400">
              Filters active
              {actionFilter  && <> · <strong className="text-surface-600">{AUDIT_ACTION_LABELS[actionFilter]}</strong></>}
              {audienceFilter && <> · <strong className="text-surface-600">{AUDIENCE_LABELS[audienceFilter as Audience]}</strong></>}
              {dateFromStr   && <> · from <strong className="text-surface-600">{dateFromStr}</strong></>}
              {dateToStr     && <> · to <strong className="text-surface-600">{dateToStr}</strong></>}
            </p>
          </div>
        )}
      </div>

      {/* ── Results ─────────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-surface-200 bg-white shadow-card overflow-hidden">

        {isLoading ? (
          <div className="p-12 flex flex-col items-center gap-3 text-surface-400">
            <Loader2 className="h-6 w-6 animate-spin" />
            <p className="text-sm">Loading audit log…</p>
          </div>
        ) : isError ? (
          <div className="p-8 text-center text-sm text-red-500">
            Failed to load audit log. Check your permissions or composite indexes.
          </div>
        ) : entries.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardList className="h-8 w-8 text-surface-200 mx-auto mb-2" />
            <p className="text-sm text-surface-400">No audit entries match your filters.</p>
          </div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-surface-100 bg-surface-50">
              <p className="text-xs text-surface-400">
                {entries.length} entr{entries.length === 1 ? 'y' : 'ies'}
                {hasNextPage && ' (more available)'}
              </p>
            </div>

            <div className="divide-y divide-surface-50">
              {entries.map(log => (
                <AuditLogRow key={log.id} log={log} />
              ))}
            </div>

            {hasNextPage && (
              <div className="px-5 py-4 border-t border-surface-100 bg-surface-50">
                <Button
                  variant="secondary"
                  size="sm"
                  loading={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                  fullWidth
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Row component ─────────────────────────────────────────────────────────────

function AuditLogRow({ log }: { log: AuditLog }) {
  const group = getActionGroup(log.action);
  const label = AUDIT_ACTION_LABELS[log.action] ?? log.action;
  const isContentAction = log.action.startsWith('content') ||
                          log.action.startsWith('audio') ||
                          log.action.startsWith('image');

  return (
    <div className="flex items-start gap-4 px-5 py-4 hover:bg-surface-50 transition-colors">

      {/* Icon */}
      <div className={cn(
        'shrink-0 flex h-8 w-8 items-center justify-center rounded-full mt-0.5',
        'bg-surface-100 text-surface-500',
      )}>
        <ActionIcon action={log.action} />
      </div>

      {/* Body */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-semibold text-surface-900">{log.userDisplayName}</span>
          <span className="text-sm text-surface-500">{label.toLowerCase()}</span>

          {/* Action group badge */}
          <span className="flex items-center gap-1 text-[10px] text-surface-400">
            <span className={cn('h-1.5 w-1.5 rounded-full inline-block', GROUP_DOT[group])} />
            {group}
          </span>
        </div>

        {/* Context */}
        <div className="flex flex-wrap items-center gap-2 mt-1">
          {log.audience && (
            <span className="flex items-center gap-1 text-xs text-surface-500">
              <span className={cn('h-2 w-2 rounded-full', AUDIENCE_DOT[log.audience as Audience])} />
              {AUDIENCE_LABELS[log.audience as Audience]}
            </span>
          )}
          {log.dayNumber != null && (
            <span className="text-xs text-surface-400">
              Day {log.dayNumber}
              {log.weekNumber  && ` · Wk ${log.weekNumber}`}
              {log.monthNumber && ` · Mo ${log.monthNumber}`}
            </span>
          )}
          {log.fieldsChanged.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {log.fieldsChanged.slice(0, 6).map(f => (
                <span key={f}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-surface-100 text-surface-500">
                  {f}
                </span>
              ))}
              {log.fieldsChanged.length > 6 && (
                <span className="text-[10px] text-surface-400">
                  +{log.fieldsChanged.length - 6} more
                </span>
              )}
            </div>
          )}
        </div>

        {/* Timestamp + email */}
        <p className="text-[11px] text-surface-400 mt-1">
          {formatDisplayDate(log.timestamp)}
          <span className="mx-1">·</span>
          {log.userEmail}
        </p>
      </div>

      {/* Quick link to the day */}
      {isContentAction && log.audience && log.dayNumber != null && (
        <Link
          to={`/content/${log.audience}/day/${log.dayNumber}`}
          className="shrink-0 flex items-center gap-1 text-xs font-medium
                     text-primary-500 hover:text-primary-700 transition-colors mt-0.5"
        >
          Edit
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}
